import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import {
  IconBolt,
  IconBookOpen,
  IconChartBarSquare,
  IconChevronDown,
  IconDeviceMobile,
  IconGift,
  IconGlobe,
  IconUsers,
  IconUserPlus,
  IconHome,
  IconLink,
  IconMenu,
  IconPaperAirplane,
  IconPuzzle,
  IconSticker,
  IconX,
  Spinner,
} from '@/components';

type AdminTab = {
  to: string;
  label: string;
  match: (p: string) => boolean;
  icon: ReactNode;
};

type AdminNavGroup = {
  id: string;
  label: string;
  items: AdminTab[];
};

const overviewTab: AdminTab = {
  to: '/admin',
  label: 'Overview',
  match: (p) => p === '/admin' || p === '/admin/',
  icon: <IconHome className="text-current" />,
};

const toolsTab: AdminTab = {
  to: '/admin/engagement',
  label: 'Tools',
  match: (p) => p.startsWith('/admin/engagement'),
  icon: <IconPaperAirplane className="text-current" />,
};

const navGroups: AdminNavGroup[] = [
  {
    id: 'ops',
    label: 'Ops',
    items: [
      {
        to: '/admin/support',
        label: 'Support',
        match: (p) => p.startsWith('/admin/support'),
        icon: <IconPaperAirplane className="text-current" />,
      },
      {
        to: '/admin/feedback',
        label: 'Feedback',
        match: (p) => p.startsWith('/admin/feedback'),
        icon: <IconPaperAirplane className="text-current" />,
      },
      {
        to: '/admin/devices',
        label: 'Devices',
        match: (p) => p.startsWith('/admin/devices'),
        icon: <IconDeviceMobile className="text-current" />,
      },
      {
        to: '/admin/users',
        label: 'Users',
        match: (p) => p.startsWith('/admin/users'),
        icon: <IconUsers className="text-current" />,
      },
      {
        to: '/admin/donations',
        label: 'Donations',
        match: (p) => p.startsWith('/admin/donations'),
        icon: <IconGift className="text-current" />,
      },
      {
        to: '/admin/activity',
        label: 'Activity',
        match: (p) => p.startsWith('/admin/activity'),
        icon: <IconBolt className="text-current" />,
      },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    items: [
      {
        to: '/admin/games',
        label: 'Games',
        match: (p) => p.startsWith('/admin/games'),
        icon: <IconPuzzle className="text-current" />,
      },
      {
        to: '/admin/crosswords',
        label: 'Crosswords',
        match: (p) => p.startsWith('/admin/crosswords'),
        icon: <IconPuzzle className="text-current" />,
      },
      {
        to: '/admin/village-maps',
        label: 'Village Maps',
        match: (p) => p.startsWith('/admin/village-maps'),
        icon: <IconPuzzle className="text-current" />,
      },
      {
        to: '/admin/sentence-puzzles',
        label: 'Sentences',
        match: (p) => p.startsWith('/admin/sentence-puzzles'),
        icon: <IconPuzzle className="text-current" />,
      },
      {
        to: '/admin/dictionary',
        label: 'Dictionary',
        match: (p) => p.startsWith('/admin/dictionary'),
        icon: <IconBookOpen className="text-current" />,
      },
      {
        to: '/admin/sticker-packs',
        label: 'Stickers',
        match: (p) => p.startsWith('/admin/sticker-packs'),
        icon: <IconSticker className="text-current" />,
      },
      {
        to: '/admin/flashcard-sets',
        label: 'Flashcards',
        match: (p) => p.startsWith('/admin/flashcard-sets'),
        icon: <IconBookOpen className="text-current" />,
      },
      {
        to: '/admin/term-sheets',
        label: 'Term Sheets',
        match: (p) => p.startsWith('/admin/term-sheets'),
        icon: <IconBookOpen className="text-current" />,
      },
      {
        to: '/admin/storybooks',
        label: 'Storybooks',
        match: (p) => p.startsWith('/admin/storybooks'),
        icon: <IconBookOpen className="text-current" />,
      },
      {
        to: '/admin/featured-resources',
        label: 'Hero',
        match: (p) => p.startsWith('/admin/featured-resources'),
        icon: <IconLink className="text-current" />,
      },
    ],
  },
  {
    id: 'config',
    label: 'Config',
    items: [
      {
        to: '/admin/feature-flags',
        label: 'Flags',
        match: (p) => p.startsWith('/admin/feature-flags'),
        icon: <IconBolt className="text-current" />,
      },
      {
        to: '/admin/app-version',
        label: 'App Version',
        // Exact path only — do not match Analytics → App Versions (`/admin/app-versions`).
        match: (p) => p === '/admin/app-version' || p.startsWith('/admin/app-version/'),
        icon: <IconDeviceMobile className="text-current" />,
      },
      {
        to: '/admin/admin-users',
        label: 'Admins',
        match: (p) => p.startsWith('/admin/admin-users'),
        icon: <IconUserPlus className="text-current" />,
      },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    items: [
      {
        to: '/admin/cohorts',
        label: 'Cohorts',
        match: (p) => p.startsWith('/admin/cohorts'),
        icon: <IconChartBarSquare className="text-current" />,
      },
      {
        to: '/admin/acquisition',
        label: 'Acquisition',
        match: (p) => p.startsWith('/admin/acquisition'),
        icon: <IconChartBarSquare className="text-current" />,
      },
      {
        to: '/admin/metrics',
        label: 'Metrics',
        match: (p) => p.startsWith('/admin/metrics'),
        icon: <IconChartBarSquare className="text-current" />,
      },
      {
        to: '/admin/web',
        label: 'Web',
        match: (p) => p.startsWith('/admin/web'),
        icon: <IconGlobe className="text-current" />,
      },
      {
        to: '/admin/app-versions',
        label: 'App Versions',
        match: (p) => p.startsWith('/admin/app-versions'),
        icon: <IconDeviceMobile className="text-current" />,
      },
      {
        to: '/admin/learning',
        label: 'Learning',
        match: (p) => p.startsWith('/admin/learning'),
        icon: <IconBookOpen className="text-current" />,
      },
      {
        to: '/admin/streaks',
        label: 'Streaks',
        match: (p) => p.startsWith('/admin/streaks'),
        icon: <IconBolt className="text-current" />,
      },
      {
        to: '/admin/analytics/sessions',
        label: 'Sessions',
        match: (p) => p.startsWith('/admin/analytics/sessions'),
        icon: <IconChartBarSquare className="text-current" />,
      },
      {
        to: '/admin/analytics/timeline',
        label: 'Timeline',
        match: (p) => p.startsWith('/admin/analytics/timeline'),
        icon: <IconChartBarSquare className="text-current" />,
      },
      {
        to: '/admin/analytics/pulse',
        label: 'Pulse',
        match: (p) => p.startsWith('/admin/analytics/pulse'),
        icon: <IconBolt className="text-current" />,
      },
    ],
  },
];

const allTabs: AdminTab[] = [overviewTab, ...navGroups.flatMap((g) => g.items), toolsTab];

function tabLinkClass(active: boolean, compact = false) {
  return clsx(
    compact
      ? 'inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium transition-colors'
      : 'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
    active
      ? compact
        ? 'bg-primary/12 text-primary ring-1 ring-primary/25'
        : 'bg-primary/12 text-primary'
      : compact
        ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
  );
}

function AdminNavDropdown({
  group,
  pathname,
  open,
  onToggle,
  onClose,
}: {
  group: AdminNavGroup;
  pathname: string;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const activeItem = group.items.find((t) => t.match(pathname));
  const groupActive = Boolean(activeItem);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        onCloseRef.current();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium transition-colors',
          groupActive || open
            ? 'bg-primary/12 text-primary ring-1 ring-primary/25'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={onToggle}
      >
        <span>{group.label}</span>
        {activeItem ? (
          <span className="font-normal text-primary/70">· {activeItem.label}</span>
        ) : null}
        <IconChevronDown
          size={14}
          className={clsx('opacity-70 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open ? (
        <div
          id={menuId}
          role="menu"
          className="absolute left-0 top-full z-50 mt-1.5 min-w-[12.5rem] rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg"
        >
          {group.items.map((t) => {
            const active = t.match(pathname);
            return (
              <Link
                key={t.to}
                to={t.to}
                role="menuitem"
                className={clsx(
                  'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  active
                    ? 'bg-primary/12 text-primary'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                )}
                onClick={onClose}
              >
                <span className="opacity-80">{t.icon}</span>
                {t.label}
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function AdminLayout() {
  const { isReady, isAuthed, isAnon, meError, refetchSession, logout } = useAdminAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);

  useEffect(() => {
    if (isReady && isAnon) {
      void navigate({ to: '/login' });
    }
  }, [isReady, isAnon, navigate]);

  useEffect(() => {
    setMenuOpen(false);
    setOpenGroupId(null);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  if (!isReady) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (isReady && !isAuthed && !isAnon) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6">
        <p className="text-sm text-red-600">
          Could not verify session: {meError?.detail ?? meError?.message ?? 'Unknown error'}
        </p>
        <button
          type="button"
          className="rounded border border-gray-300 px-4 py-2 text-sm"
          onClick={() => {
            void refetchSession();
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!isAuthed) {
    return null;
  }

  const adminIconSrc = `${import.meta.env.BASE_URL}app-icon.png`;
  const activeTab = allTabs.find((t) => t.match(pathname));

  const signOut = () => {
    logout.mutate(undefined, {
      onSettled: () => {
        void navigate({ to: '/login' });
      },
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/95 shadow-sm backdrop-blur">
        <div className="relative z-50 flex items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">
          <Link
            to={overviewTab.to}
            className="flex min-w-0 items-center gap-2.5 rounded-xl transition-colors hover:bg-gray-50"
            onClick={() => {
              setMenuOpen(false);
              setOpenGroupId(null);
            }}
          >
            <img
              src={adminIconSrc}
              alt=""
              width={36}
              height={36}
              decoding="async"
              className="h-9 w-9 shrink-0 rounded-xl object-cover shadow-sm ring-1 ring-gray-900/8"
            />
            <div className="min-w-0">
              <span className="block truncate text-lg font-semibold tracking-tight text-gray-900 sm:text-xl">
                Hmonglus Admin
              </span>
              {activeTab ? (
                <span className="block truncate text-xs text-gray-500 lg:hidden">{activeTab.label}</span>
              ) : null}
            </div>
          </Link>

          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              className="hidden text-sm text-gray-600 hover:text-gray-900 lg:inline"
              onClick={signOut}
            >
              Sign out
            </button>
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100 lg:hidden"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="admin-mobile-nav"
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <IconX size={22} /> : <IconMenu size={22} />}
            </button>
          </div>
        </div>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 px-4 pb-4 sm:px-6 lg:flex" aria-label="Admin">
          <Link
            to={overviewTab.to}
            className={tabLinkClass(overviewTab.match(pathname), true)}
            onClick={() => setOpenGroupId(null)}
          >
            <span className="opacity-80">{overviewTab.icon}</span>
            {overviewTab.label}
          </Link>
          {navGroups.map((group) => (
            <AdminNavDropdown
              key={group.id}
              group={group}
              pathname={pathname}
              open={openGroupId === group.id}
              onToggle={() =>
                setOpenGroupId((current) => (current === group.id ? null : group.id))
              }
              onClose={() => setOpenGroupId(null)}
            />
          ))}
          <Link
            to={toolsTab.to}
            className={tabLinkClass(toolsTab.match(pathname), true)}
            onClick={() => setOpenGroupId(null)}
          >
            <span className="opacity-80">{toolsTab.icon}</span>
            {toolsTab.label}
          </Link>
        </nav>

        {/* Mobile menu panel */}
        {menuOpen ? (
          <>
            <div
              className="fixed inset-0 z-30 bg-black/30 lg:hidden"
              aria-hidden
              onClick={() => setMenuOpen(false)}
            />
            <nav
              id="admin-mobile-nav"
              className="absolute inset-x-0 top-full z-40 max-h-[min(70vh,calc(100dvh-4.5rem))] overflow-y-auto border-b border-gray-200 bg-white shadow-lg lg:hidden"
              aria-label="Admin"
            >
              <ul className="flex flex-col gap-1 p-2">
                <li>
                  <Link
                    to={overviewTab.to}
                    className={tabLinkClass(overviewTab.match(pathname))}
                    onClick={() => setMenuOpen(false)}
                  >
                    <span className="opacity-80">{overviewTab.icon}</span>
                    {overviewTab.label}
                  </Link>
                </li>
                {navGroups.map((group) => (
                  <li key={group.id} className="pt-1">
                    <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      {group.label}
                    </div>
                    <ul className="flex flex-col">
                      {group.items.map((t) => {
                        const active = t.match(pathname);
                        return (
                          <li key={t.to}>
                            <Link
                              to={t.to}
                              className={tabLinkClass(active)}
                              onClick={() => setMenuOpen(false)}
                            >
                              <span className="opacity-80">{t.icon}</span>
                              {t.label}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ))}
                <li className="pt-1">
                  <Link
                    to={toolsTab.to}
                    className={tabLinkClass(toolsTab.match(pathname))}
                    onClick={() => setMenuOpen(false)}
                  >
                    <span className="opacity-80">{toolsTab.icon}</span>
                    {toolsTab.label}
                  </Link>
                </li>
              </ul>
              <div className="border-t border-gray-100 p-2">
                <button
                  type="button"
                  className="flex w-full items-center rounded-xl px-3 py-3 text-left text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  onClick={signOut}
                >
                  Sign out
                </button>
              </div>
            </nav>
          </>
        ) : null}
      </header>
      <main
        className={clsx(
          'mx-auto w-full p-4 sm:p-6',
          pathname.startsWith('/admin/village-maps')
            ? 'max-w-[1800px]'
            : 'max-w-7xl',
        )}
      >
        <Outlet />
      </main>
    </div>
  );
}

export const Route = createFileRoute('/admin')({ component: AdminLayout });
